/**
 * MyCopro - Configuration Firebase
 * ✅ MISE À JOUR: Ajout de Firebase Cloud Messaging (FCM)
 * ✅ getMessagingInstance() vérifie le support avant initialisation
 */

import { initializeApp } from 'firebase/app';
import { getAuth, initializeAuth, browserLocalPersistence, inMemoryPersistence } from 'firebase/auth';
import { getFirestore, initializeFirestore, persistentLocalCache, persistentMultipleTabManager } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { getMessaging, isSupported, Messaging } from 'firebase/messaging';
import { Capacitor } from '@capacitor/core';
import { logger } from '@/utils/logger';

// Configuration Firebase - Remplacer par vos valeurs
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyDf34G5yc3LH4kB3o9pp-XLY8rbx3aZRYE",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "mycopro-fb5fd.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "mycopro-fb5fd",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "mycopro-fb5fd.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "830480201958",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:830480201958:web:0a40ddc9b4cb48f44d6327"
};

// Initialiser Firebase
const app = initializeApp(firebaseConfig);

// Services Firebase
// ✅ Sur iOS natif (WKWebView, scheme capacitor://), la détection auto de
// persistence de getAuth() peut rester bloquée sur IndexedDB indéfiniment
// (onAuthStateChanged ne se déclenche jamais). On force explicitement
// localStorage, qui répond de façon fiable dans ce contexte.
export const auth = Capacitor.getPlatform() === 'ios'
  ? initializeAuth(app, { persistence: [browserLocalPersistence, inMemoryPersistence] })
  : getAuth(app);

// ✅ Cache local persistant (IndexedDB) : permet la consultation hors-ligne
// (lectures déjà visitées servies depuis le cache, écritures mises en file d'attente).
// initializeFirestore() ne peut être appelé qu'une fois par app — en dev avec HMR,
// un second appel lève une erreur ; on se rabat alors sur l'instance déjà créée.
export let db: ReturnType<typeof getFirestore>;
try {
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() })
  });
} catch (error) {
  logger.db('Firestore déjà initialisé, réutilisation de l\'instance existante', error);
  db = getFirestore(app);
}

export const storage = getStorage(app);
export const functions = getFunctions(app, 'europe-west9');

// ============================================================================
// ✅ FIREBASE CLOUD MESSAGING (FCM)
// ============================================================================

/**
 * Instance messaging mise en cache après première initialisation.
 * null = pas encore initialisé, false = non supporté sur cette plateforme.
 */
let messagingInstance: Messaging | null | false = null;

/**
 * Récupère l'instance Firebase Messaging.
 * Vérifie d'abord que FCM est supporté (navigateur avec Service Workers).
 * Sur Android/Capacitor, FCM passe par le plugin natif, pas par le SDK web.
 * Retourne null si non supporté (ex: Safari iOS, ou contexte Capacitor Android).
 */
export const getMessagingInstance = async (): Promise<Messaging | null> => {
  // Sur Android/iOS natif, FCM passe par le plugin Capacitor — jamais le SDK web
  if (Capacitor.isNativePlatform()) return null;

  // Retourner le cache si déjà résolu
  if (messagingInstance === false) return null;
  if (messagingInstance !== null) return messagingInstance;

  try {
    const supported = await isSupported();
    if (supported) {
      messagingInstance = getMessaging(app);
      return messagingInstance;
    } else {
      logger.fcm('FCM web non supporté sur cette plateforme');
      messagingInstance = false;
      return null;
    }
  } catch (error) {
    console.error('[Firebase] Erreur initialisation FCM:', error);
    messagingInstance = false;
    return null;
  }
};

export default app;
